import { useStrings } from "@1js/localization-components";
import {
  Button,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type MenuProps,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { MenuFilterPlaceholderValueLabel } from "../PromptFilter.strings";
import { useStyles } from "../PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilterSkeleton";
import {
  CLEAR_FILTER_TOKEN,
  FILTER_CONCATENATE_TOKEN,
  type Filter,
} from "../types";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
import { groupFilters } from "../utils";
import type { FilterProps } from "./Filter.types";
import { RenderMenuOptions } from "./RenderMenuOptions";
import { SubMenuFilter } from "./SubMenuFilter";

const findFilterOptionDisplayLabelsFromSelectedValues = (
  selectedValues: string[],
  filter: Filter,
  selectedValueToLabel: (value: string) => string
): string[] => {
  return selectedValues.map((selectedValue: string) => {
    let filterOption;
    const selectedValueSplit = selectedValue.split(FILTER_CONCATENATE_TOKEN)[0];
    if (filter.type === "nested") {
      // loop through all the nested filter options
      filter.submenuGroups?.forEach((submenuGroup) => {
        submenuGroup.nestedOptions.forEach((nestedFilter) => {
          filterOption = nestedFilter.options.find(
            (option) => option.value === selectedValueSplit
          );
        });
      });
    } else {
      filterOption = filter.options.find(
        (option) => option.value === selectedValueSplit
      );
    }
    if (filterOption?.displayLabel) {
      return filterOption.displayLabel;
    }

    return selectedValueToLabel(selectedValueSplit);
  });
};

const concatenateSelectedValues = (
  selectedValues: string[] | undefined,
  filter: Filter,
  selectedValueToLabel: (value: string) => string
): string => {
  if (!selectedValues || selectedValues.length === 0) {
    return "";
  }
  const valueLabels = findFilterOptionDisplayLabelsFromSelectedValues(
    selectedValues,
    filter,
    selectedValueToLabel
  );
  if (valueLabels.length > 1) {
    return valueLabels[0] + " + " + (valueLabels.length - 1);
  }
  return valueLabels.join(", ");
};

const addSelectAll = (checkedValues: Record<string, string[]>) => {
  const result = { ...checkedValues };
  if (
    Object.entries(result).every(([_key, value]) => value.length < 1) ||
    Object.keys(result).length < 1
  ) {
    result[`${CLEAR_FILTER_TOKEN}`] = [`${CLEAR_FILTER_TOKEN}`];
  }
  return result;
};

export const MenuFilter: React.FC<FilterProps> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
  onOpenChanged,
  isMetaOsV2,
  isRockSteadyUxEnabled,
  disabledFilters,
  selectedFilterValue,
  registerClearCallback,
  deregisterClearCallback,
}) => {
  const styles = useStyles(appearance)();
  const [selectedValuesControlled, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );

  React.useEffect(() => {
    if (registerClearCallback && deregisterClearCallback) {
      registerClearCallback(filter.key, () => {
        setValues([]);
        setCheckedValues({});
      });
    }
    return () => {
      if (deregisterClearCallback) {
        deregisterClearCallback(filter.key);
      }
    };
  }, []); // only run on mount and unmount

  const isInactiveFilter = React.useMemo(() => {
    if (!selectedValuesControlled || selectedValuesControlled.length === 0) {
      return true; // No values selected, treat as inactive
    }
    return selectedValuesControlled.every((value) => {
      const [firstToken] = value.split(FILTER_CONCATENATE_TOKEN);
      return firstToken === CLEAR_FILTER_TOKEN; // Check if all values are "Select All"
    });
  }, [selectedValuesControlled]);

  const localizations = useStrings({
    MenuFilterPlaceholderValueLabel,
  });

  const {
    facetKeyToLabel,
    facetValueToLabel,
    selectedValueToLabel,
    groupValueToLabel,
    nestedGroupValueToLabel,
  } = usePromptFilterLocalizations();

  const filterLabel = facetKeyToLabel(
    filter.key,
    isMetaOsV2 || isRockSteadyUxEnabled
  );

  const placeholderValue = localizations.MenuFilterPlaceholderValueLabel();

  const filterByGroup = groupFilters(filter);

  const [checkedValues, setCheckedValues] = React.useState<
    Record<string, string[]>
  >({});

  const onChangeChecked: MenuProps["onCheckedValueChange"] = (
    _e,
    { name, checkedItems }: { name: string; checkedItems: string[] }
  ) => {
    // Checking if the value selected is the "Select All" option
    const selectedNameSplit = name.split(FILTER_CONCATENATE_TOKEN);
    const firstNameToken = selectedNameSplit.shift();
    const secondNameToken =
      selectedNameSplit.length > 1 ? selectedNameSplit[1] : "";
    if (firstNameToken === CLEAR_FILTER_TOKEN) {
      // if Select All is the top level, we just clear all the values
      if (secondNameToken === "" || secondNameToken === "NA") {
        setCheckedValues({});
        setValues([]);
        return;
      }
      const clearGroupKey = selectedNameSplit.join(FILTER_CONCATENATE_TOKEN);

      // If the "Select All" option is selected, then we need to clear all the values in that group
      setCheckedValues((prevCheckedValues) => {
        const copyObj = { ...prevCheckedValues };
        Object.keys(copyObj).forEach((key) => {
          const checkedValueSplit = copyObj[key][0]?.split(
            FILTER_CONCATENATE_TOKEN
          );
          checkedValueSplit?.shift();
          const valGroupKey = checkedValueSplit?.join(FILTER_CONCATENATE_TOKEN);
          if (clearGroupKey === valGroupKey) {
            delete copyObj[key];
          }
        });

        return {
          ...copyObj,
        };
      });

      setValues((prev) => {
        if (prev === undefined) {
          return [];
        }
        const newControlledValues = [...prev].filter((item) => {
          const itemSplit = item.split(FILTER_CONCATENATE_TOKEN);
          itemSplit.shift();
          const valJoined = itemSplit.join(FILTER_CONCATENATE_TOKEN);
          return valJoined !== clearGroupKey;
        });
        return newControlledValues.filter(
          (item) => !item.includes(CLEAR_FILTER_TOKEN)
        );
      });
      return;
    }
    setCheckedValues((prevCheckedValues) => ({
      ...prevCheckedValues,
      [name]: checkedItems,
    }));

    const allCheckedItems = Object.values({
      ...checkedValues,
      [name]: checkedItems,
    }).flat();

    setValues(allCheckedItems);
  };

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const displayedValueText =
    (disabledFilters?.[filter.key] &&
      selectedFilterValue &&
      selectedValueToLabel(selectedFilterValue)) || // Show selectedFilterValue only if the filter is disabled
    concatenateSelectedValues(
      selectedValuesControlled,
      filter,
      selectedValueToLabel
    );

  const handleOnOpenChanged = React.useCallback(
    (
      _e:
        | React.MouseEvent<HTMLElement>
        | React.KeyboardEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
        | MouseEvent
        | KeyboardEvent
        | TouchEvent,
      data: { open: boolean }
    ) => {
      setIsMenuOpen(data.open);
      onOpenChanged && onOpenChanged(data.open, [filter]);
    },
    [filter, onOpenChanged]
  );

  const selectedWithSelectAll = React.useMemo(() => {
    return addSelectAll(checkedValues);
  }, [checkedValues]);

  if (filter.options.length < 1 && filter.type !== "nested") {
    return null;
  }
  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Menu
          positioning={{ autoSize: true }}
          onOpenChange={handleOnOpenChanged}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              shape="circular"
              disabled={disabledFilters?.[filter.key] || filter.disabled}
              data-testid={`${filter.key}-menu`}
              className={`${
                disabledFilters?.[filter.key] || filter.disabled
                  ? styles.disabledButton
                  : isInactiveFilter
                    ? `${
                        isMenuOpen
                          ? styles.inactiveFilterButtonSelected
                          : styles.inactiveFilterButton
                      }` // Styling for inactive filter
                    : `${
                        isMenuOpen
                          ? styles.activeFilterButtonSelected
                          : styles.activeFilterButton
                      }` // Styling for active filter
              }`}
            >
              <span>
                <span
                  className={`${
                    disabledFilters?.[filter.key] || filter.disabled
                      ? styles.disabledButtonFilterLabel
                      : isInactiveFilter
                        ? `${
                            isMenuOpen
                              ? styles.inactiveFilterLabelSelected
                              : styles.inactiveFilterLabel
                          }`
                        : `${
                            isMenuOpen
                              ? styles.activeFilterLabelSelected
                              : styles.activeFilterLabel
                          }`
                  }`}
                >
                  {filterLabel}:{" "}
                </span>
                <span
                  className={`${
                    disabledFilters?.[filter.key] || filter.disabled
                      ? styles.disabledButtonDisplayedValueText
                      : isInactiveFilter
                        ? `${
                            isMenuOpen
                              ? styles.inactiveDisplayedValueTextSelected
                              : styles.inactiveDisplayedValueText
                          }`
                        : `${
                            isMenuOpen
                              ? styles.activeDisplayedValueTextSelected
                              : styles.activeDisplayedValueText
                          }`
                  }`}
                >
                  {displayedValueText || placeholderValue}
                </span>
              </span>
            </Button>
          </MenuTrigger>

          <MenuPopover>
            <MenuList
              checkedValues={selectedWithSelectAll}
              onCheckedValueChange={onChangeChecked}
            >
              {filter.type === "nested" && filter.submenuGroups ? (
                <>
                  {filter.submenuGroups.map((submenuGroup, ind, self) => (
                    <MenuGroup
                      key={submenuGroup.groupLabel}
                      data-testid="option-group"
                    >
                      <MenuGroupHeader>
                        {nestedGroupValueToLabel(submenuGroup.groupLabel)}
                      </MenuGroupHeader>
                      {submenuGroup.nestedOptions.map((nestedFilter) => (
                        <SubMenuFilter
                          key={nestedFilter.displayLabel}
                          filterOptions={nestedFilter.options}
                          triggerDisplayLabel={nestedFilter.displayLabel}
                          groupDisplayLabel={submenuGroup.groupLabel}
                          selectedValues={selectedValuesControlled}
                          onChangeChecked={onChangeChecked}
                          checkedValues={checkedValues}
                          isDisabled={submenuGroup.disabled}
                        />
                      ))}
                      {ind !== self.length - 1 && <MenuDivider />}
                    </MenuGroup>
                  ))}
                  <MenuDivider />
                  <MenuItem
                    onClick={() => {
                      setCheckedValues({});
                      setValues([]);
                    }}
                  >
                    {facetValueToLabel("ClearAll")}
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItemCheckbox
                    name={`${CLEAR_FILTER_TOKEN}`}
                    value={`${CLEAR_FILTER_TOKEN}`}
                    key={"Select All"}
                    aria-label={`selectall-menuitemcheckbox`}
                    aria-checked={selectedValues?.includes(
                      `${CLEAR_FILTER_TOKEN}`
                    )}
                  >
                    {facetValueToLabel(CLEAR_FILTER_TOKEN)}
                  </MenuItemCheckbox>
                  {Object.entries(filterByGroup)
                    .sort((a, _b) => {
                      if (a[0] === "FromOrganization") {
                        return -1;
                      } else {
                        return -1;
                      }
                    })
                    .map(([groupKey, filterOptions], ind, self) => (
                      <React.Fragment key={groupKey}>
                        <RenderMenuOptions
                          groupKey={groupKey}
                          groupLabel={groupValueToLabel(groupKey)}
                          filterOptions={filterOptions}
                          selectedValues={checkedValues}
                          valueToLabel={facetValueToLabel}
                          showGroupHeader={self.length > 1}
                          appearance={appearance}
                        />
                        {ind !== self.length - 1 && <MenuDivider />}
                      </React.Fragment>
                    ))}
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    </React.Fragment>
  );
};
